import React, { useRef } from "react"
import { useEffect, useState } from "react"
import { DataTableSortStatus } from "mantine-datatable"
import sortBy from "lodash/sortBy"
import { TAgetAgencyApplications, TAsetPricing } from "../../../services/applicationAPI"
import { columnConfig } from "./components/columns"
import { applicationStateLabels, applicationStates } from "../../Campaign/findCampaign/FindCampaign"
import { useNavigate } from "react-router-dom"
import StickyDataTable from "../../../components/StickyDataTable"
import { UpdateApplicationModal } from "../../../components/UpdateApplicationModal"
import { UpdateAddressModal } from "../../../components/UpdateAddressModal"
import EditUserPriceBrand from "../../Campaign/findCampaign/components/editPriceUserBrand"
import { getErrorStatus } from "../../../types/error"

const AgencyApplications = () => {
  const navigate = useNavigate()
  const abortControllerRef = useRef<AbortController | null>(null)
  const [page, setPage] = useState(1)
  const PAGE_SIZES = [10, 20, 30, 50, 100, 500, 1000]
  const [pageSize, setPageSize] = useState(PAGE_SIZES[2])
  const [totalDocuments, setTotalDocuments] = useState(0)
  const [initialRecords, setInitialRecords] = useState(sortBy([], "id"))
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [sortByColumn, setSortByColumn] = useState("")
  const [sortOrder, setSortOrder] = useState(true)
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus>({
    columnAccessor: "id",
    direction: "asc",
  })
  const [state, setState] = useState("")
  const [isExpanded, setIsExpanded] = useState(false)
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState<any>(null)
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const [selectedApplicationForAddress, setSelectedApplicationForAddress] = useState<any>(null)
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false)
  const [selectedApplicationForPricing, setSelectedApplicationForPricing] = useState<any>(null)
  const [newPricingUser, setNewPricingUser] = useState<number>(0)
  const [newPricingBrand, setNewPricingBrand] = useState<number>(0)

  const handleFetchData = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setLoading(true)
    setError(null)
    try {
      const response = await TAgetAgencyApplications(
        page,
        pageSize,
        sortByColumn,
        sortOrder,
        state,
        abortControllerRef.current.signal,
      )
      let data = []
      let totalRecords = 0

      totalRecords = response.totalCount?.[0]?.count ?? 0
      data = response.data.map((item: any) => ({
        ...item,
      }))

      setInitialRecords(data)
      setTotalDocuments(totalRecords)
      setLoading(false)
    } catch (error: unknown) {
      setLoading(false)
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error fetching data, ${error}`)
    }
  }

  useEffect(() => {
    handleFetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortByColumn, sortOrder, state])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  useEffect(() => {
    setSortStatus({
      columnAccessor: sortByColumn || "id",
      direction: sortOrder ? "desc" : "asc",
    })
  }, [sortByColumn, sortOrder])

  const handleSorting = (columnAccessor: string) => {
    if (columnAccessor === sortByColumn) {
      setSortOrder((prevOrder) => !prevOrder)
    } else {
      setSortByColumn(columnAccessor)
      setSortOrder(true)
    }
  }

  const handleUpdateApplication = (application: any) => {
    setSelectedApplication(application)
    setIsUpdateModalOpen(true)
  }

  const handleCloseUpdateModal = () => {
    setIsUpdateModalOpen(false)
    setSelectedApplication(null)
  }

  const handleUpdateAddress = (application: any) => {
    setSelectedApplicationForAddress(application)
    setIsAddressModalOpen(true)
  }

  const handleCloseAddressModal = () => {
    setIsAddressModalOpen(false)
    setSelectedApplicationForAddress(null)
  }

  const handleUpdatePricing = (application: any) => {
    setSelectedApplicationForPricing(application)
    setNewPricingUser(application.price_user || 0)
    setNewPricingBrand(application.price_brand || 0)
    setIsPricingModalOpen(true)
  }

  const handleClosePricingModal = () => {
    setIsPricingModalOpen(false)
    setSelectedApplicationForPricing(null)
    setNewPricingUser(0)
    setNewPricingBrand(0)
  }

  const handleSavePricingData = async () => {
    if (!selectedApplicationForPricing?._id) {
      return
    }

    try {
      const response = await TAsetPricing(
        newPricingUser,
        newPricingBrand,
        selectedApplicationForPricing._id,
      )
      if (response) {
        handleClosePricingModal()
        handleFetchData()
      }
    } catch (error) {
      alert("Fiyat güncellenirken hata oluştu")
    }
  }

  const renderStatePayload = (application: any) => {
    const toggleExpandedRow = () => {
      setIsExpanded(!isExpanded)
    }
    if (isExpanded) {
      return (
        <div>
          {application.state_payload}
          <button
            type="button"
            onClick={toggleExpandedRow}
            className="text-blue-500 hover:underline"
          >
            Less
          </button>
        </div>
      )
    } else if (application.state_payload?.length > 20) {
      return (
        <div>
          {`${application.state_payload.slice(0, 20)}... `}
          <button
            type="button"
            onClick={toggleExpandedRow}
            className="text-blue-500 hover:underline"
          >
            More
          </button>
        </div>
      )
    } else {
      return <div>{application.state_payload}</div>
    }
  }

  const columns = columnConfig({
    renderStatePayload,
    applicationStateLabels,
    handleUpdateApplication,
    handleUpdateAddress,
    handleUpdatePricing,
  })

  return (
    <div className="panel">
      {error && <div className="text-red-500 text-center">{error}</div>}
      <div>
        <select
          className="mb-4 p-3 border rounded"
          value={state}
          onChange={(e) => {
            setState(e.target.value)
          }}
        >
          <option value="">Tüm Aşamalar</option>
          {applicationStates.map((state) => (
            <option key={state} value={state}>
              {applicationStateLabels[state] || state}
            </option>
          ))}
        </select>
      </div>
      <div className="datatables">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
          </div>
        ) : (
          <StickyDataTable
            records={initialRecords}
            columns={columns}
            totalRecords={totalDocuments}
            recordsPerPage={pageSize}
            page={page}
            onPageChange={(p) => setPage(p)}
            recordsPerPageOptions={PAGE_SIZES}
            onRecordsPerPageChange={setPageSize}
            sortStatus={sortStatus}
            onSortStatusChange={(e) => handleSorting(e.columnAccessor)}
            minHeight={200}
            paginationText={({ from, to, totalRecords }) =>
              `Showing  ${from} to ${to} of ${totalRecords} entries`
            }
            highlightOnHover={true}
            className="whitespace-nowrap"
          />
        )}
      </div>

      {/* Update Application Modal */}
      {selectedApplication && (
        <UpdateApplicationModal
          isOpen={isUpdateModalOpen}
          onClose={handleCloseUpdateModal}
          _id={selectedApplication._id}
          campaign_id={selectedApplication.campaign}
          state={selectedApplication.state}
          application={selectedApplication}
        />
      )}

      {/* Update Address Modal */}
      {selectedApplicationForAddress && (
        <UpdateAddressModal
          isOpen={isAddressModalOpen}
          onClose={handleCloseAddressModal}
          applicationId={selectedApplicationForAddress._id}
          currentAddress={selectedApplicationForAddress.address}
        />
      )}

      {/* Pricing Update Modal */}
      <EditUserPriceBrand
        modalOpen={isPricingModalOpen}
        onClose={handleClosePricingModal}
        newPricingUser={newPricingUser}
        setNewPricingUser={setNewPricingUser}
        newPricingBrand={newPricingBrand}
        setNewPricingBrand={setNewPricingBrand}
        handleSavePricingData={handleSavePricingData}
        handleClosePricingModal={handleClosePricingModal}
      />
    </div>
  )
}

export default AgencyApplications
